import { motion } from "framer-motion";
import { Users, Landmark, Building2, Heart, Megaphone, Briefcase, HandHelping, Globe, LucideIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import JourneyStep from "@/components/home/JourneyStep";
import { Scale, HandCoins } from "lucide-react";

const iconMap: Record<string, LucideIcon> = {
  Megaphone, Landmark, Building2, Heart, HandHelping, Users, Globe, Briefcase,
};

const ROLE_CHIPS = [
  { label: "Advocates", icon: Megaphone, value: "Find funding for your mission" },
  { label: "Agencies", icon: Landmark, value: "Align resources with data" },
  { label: "Policymakers", icon: Scale, value: "Track community impact" },
  { label: "Funders", icon: HandCoins, value: "Maximize your dollars" },
];

const PartnerNetwork = () => {
  const { data: partners, isLoading } = useQuery({
    queryKey: ["partners"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partners")
        .select("*")
        .order("count", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  return (
    <JourneyStep
      step="03"
      label="Advocacy"
      title={<>Coalitions we've <span className="text-gradient-primary">built</span></>}
      subhead="Advocacy is a team sport. Our advocacy practice convenes the right mix of advocates, agencies, policymakers, and funders across Alabama — already connected, already ready to mobilize."
    >
      {/* Role chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
        {ROLE_CHIPS.map((r, i) => (
          <motion.div
            key={r.label}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.06 }}
            className="flex items-center gap-3 rounded-[var(--radius)] border border-border bg-card/40 px-4 py-3"
          >
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <r.icon className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="font-display text-sm font-semibold leading-tight">{r.label}</div>
              <div className="text-[11px] text-muted-foreground truncate">{r.value}</div>
            </div>
          </motion.div>
        ))}
      </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {isLoading
            ? Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-card border border-border rounded-lg p-6 text-center">
                  <Skeleton className="mx-auto w-12 h-12 rounded-full mb-4" />
                  <Skeleton className="h-4 w-20 mx-auto mb-2" />
                  <Skeleton className="h-3 w-32 mx-auto mb-3" />
                  <Skeleton className="h-8 w-10 mx-auto" />
                </div>
              ))
            : partners?.map((partner, i) => {
                const Icon = iconMap[partner.icon_name || ""] || Users;
                return (
                  <motion.div
                    key={partner.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className="group bg-card border border-border rounded-lg p-6 text-center hover:border-primary/40 hover:shadow-glow transition-all duration-300 cursor-pointer"
                  >
                    <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-display font-semibold text-sm mb-1">{partner.label}</h3>
                    <p className="text-xs text-muted-foreground mb-3">{partner.description}</p>
                    <div className="font-display text-2xl font-bold text-primary">{partner.count}</div>
                  </motion.div>
                );
              })}
        </div>
    </JourneyStep>
  );
};

export default PartnerNetwork;
